import React, { FunctionComponent } from 'react';
import Survey from '../util/Survey';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Theme, makeStyles } from '@lifeomic/chroma-react/styles';
import clsx from 'clsx';

export const useStyles = makeStyles((theme: Theme) => ({
  label: {
    color: theme.palette.primary.main,
  },
}));

export const shouldShowSurveyProgress = (
  survey: Survey,
  currentPageLink?: string,
): currentPageLink is string => {
  if (!currentPageLink || !survey.isPaginatedSurvey) return false;
  const paginationReference =
    survey.getPaginationReferenceForPage(currentPageLink);
  return (paginationReference?.totalPages ?? 0) > 1;
};

export interface SurveyProgressOwnProps {
  survey: Survey;
  currentPageLink?: string;
  variant: 'text' | 'pill';
  className?: string;
}

export type SurveyProgressProps = SurveyProgressOwnProps &
  WrappedComponentProps;

const SurveyProgress: FunctionComponent<SurveyProgressProps> = (
  props: SurveyProgressProps,
) => {
  const {
    survey,
    currentPageLink,
    intl: { formatMessage },
    className,
    variant,
  } = props;

  const classes = useStyles(props);

  if (!shouldShowSurveyProgress(survey, currentPageLink)) return null;

  const paginationReference =
    survey.getPaginationReferenceForPage(currentPageLink);

  // should always return a value, but just in case
  if (!paginationReference) return null;

  const values = {
    index: paginationReference.currentPageIndex + 1,
    total: paginationReference.totalPages,
  };
  const message =
    variant === 'pill'
      ? {
          id: 'survey.paginatedSurvey.pillProgressText',
          defaultMessage: '{index} of {total}',
        }
      : {
          id: 'survey.paginatedSurvey.labelProgress',
          defaultMessage: '{index}/{total}',
        };
  const label = formatMessage(message, values);

  if (variant === 'pill') {
    return <Pill className={className} label={label} />;
  } else {
    return (
      <Text className={clsx(className, classes.label)} size="body">
        {label}
      </Text>
    );
  }
};

export default React.memo(injectIntl(SurveyProgress));
